import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, PenSquare } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const activeTag = params.get("etiket") || "";

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: ts }, { data: arts }] = await Promise.all([
        api.get("/article-tags"),
        api.get("/articles", { params: activeTag ? { tag: activeTag } : {} }),
      ]);
      setTags(ts);
      setArticles(arts);
      setLoading(false);
    })();
  }, [activeTag]);

  const setTag = (slug) => {
    if (slug) setParams({ etiket: slug });
    else setParams({});
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-display text-6xl text-ink-900" data-testid="articles-title">
            Yazılar
          </h1>
          <p className="mt-2 font-body text-ink-700">Yavaş okumalar; ritim, malzeme, çocuk ve doğa üzerine.</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/yazilar/yeni" className="k-btn-primary" data-testid="new-article-btn">
            <PenSquare className="mr-2 h-4 w-4" /> Yeni yazı
          </Link>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="article-tag-filter">
        <button
          onClick={() => setTag("")}
          className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
            !activeTag ? "border-sage-500 bg-sage-500 text-cream-50" : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
          }`}
          data-testid="tag-all"
        >
          Tümü
        </button>
        {tags.map((t) => (
          <button
            key={t.slug}
            onClick={() => setTag(t.slug)}
            data-testid={`tag-${t.slug}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              activeTag === t.slug
                ? "border-sage-500 bg-sage-500 text-cream-50"
                : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading && [1, 2, 3].map((i) => <div key={i} className="k-card h-96 animate-pulse" />)}
        {!loading && articles.length === 0 && (
          <div className="k-card p-10 text-center md:col-span-2 lg:col-span-3" data-testid="articles-empty">
            <p className="font-body text-ink-700">
              {activeTag ? "Bu etikette henüz yazı yok." : "Henüz yazı yok. İlk okumayı yakında paylaşacağız 🌿"}
            </p>
          </div>
        )}
        {!loading &&
          articles.map((a) => <ArticleCard key={a.article_id} a={a} />)}
      </div>

      <div className="mt-16">
        <NewsletterForm source="articles-page" />
      </div>
    </div>
  );
}

export function ArticleCard({ a }) {
  return (
    <Link
      to={`/yazilar/${a.slug}`}
      className="k-card group flex flex-col overflow-hidden p-0 transition-all hover:-translate-y-1 hover:shadow-warm"
      data-testid={`article-card-${a.slug}`}
    >
      {a.cover_image_url ? (
        <img
          src={a.cover_image_url}
          alt={a.title}
          className="h-48 w-full object-cover"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : (
        <div className="h-48 w-full bg-gradient-to-br from-sage-500/25 to-earth-400/25" />
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-3 text-xs text-ink-500">
          <span>{formatDate(a.published_at)}</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {a.read_minutes} dk okuma
          </span>
        </div>
        <h3 className="mt-2 font-heading text-xl font-bold text-ink-900 group-hover:text-sage-700 transition-colors">
          {a.title}
        </h3>
        <p className="mt-2 line-clamp-3 font-body text-ink-700">{a.summary}</p>
        {a.tags?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {a.tags.slice(0, 3).map((t) => (
              <span key={t} className="k-tag !py-0.5 !text-[10px]">{t}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
